// SPDX-License-Identifier: AGPL-3.0-or-later

import {getSafeItem, setSafeItem} from '@app/features/platform/utils/SafeBrowserStorage';
import {makeAutoObservable, runInAction} from 'mobx';

export type AppCategory = 'all' | 'moderation' | 'music' | 'gaming' | 'utility' | 'entertainment';

export interface AppSlashCommand {
	readonly name: string;
	readonly description: string;
	readonly usage?: string;
}

export interface DiscoveryApp {
	readonly id: string;
	readonly name: string;
	readonly tagline: string;
	readonly description: string;
	readonly category: AppCategory;
	readonly avatar: string;
	readonly banner?: string;
	readonly verified: boolean;
	readonly featured?: boolean;
	readonly developer: {
		readonly name: string;
		readonly verified?: boolean;
	};
	readonly stats: {
		readonly serverCount: number;
		readonly userCount?: number;
	};
	readonly commands: ReadonlyArray<AppSlashCommand>;
	readonly permissions: string;
	readonly clientId: string;
	readonly supportGuildUrl?: string;
	readonly websiteUrl?: string;
	readonly privacyPolicyUrl?: string;
	readonly termsOfServiceUrl?: string;
	readonly customInstallUrl?: string;
	readonly testServerInviteUrl?: string;
	readonly testingInstructions?: string;
	readonly staffNotes?: string;
	readonly teamName?: string;
	readonly tosAgreed?: boolean;
	readonly privacyAgreed?: boolean;
	readonly teamConfirmed?: boolean;
	readonly tags: ReadonlyArray<string>;
	readonly status?: 'approved' | 'pending' | 'rejected';
}

const DEFAULT_APPS: ReadonlyArray<DiscoveryApp> = [];

const STORAGE_CUSTOM_APPS_KEY = 'flx_app_discovery_custom';

class AppDiscoveryState {
	searchQuery = '';
	selectedCategory: AppCategory = 'all';
	selectedApp: DiscoveryApp | null = null;
	customApps: Array<DiscoveryApp> = [];

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		this.loadCustomApps();
	}

	private loadCustomApps(): void {
		try {
			const stored = getSafeItem(STORAGE_CUSTOM_APPS_KEY);
			if (stored) {
				const parsed = JSON.parse(stored) as Array<DiscoveryApp>;
				if (Array.isArray(parsed)) {
					this.customApps = parsed;
				}
			}
		} catch {
			// Ignore local storage error
		}
	}

	private saveCustomApps(): void {
		try {
			setSafeItem(STORAGE_CUSTOM_APPS_KEY, JSON.stringify(this.customApps));
		} catch {
			// Ignore local storage error
		}
	}

	setSearchQuery(query: string): void {
		this.searchQuery = query;
	}

	setSelectedCategory(category: AppCategory): void {
		this.selectedCategory = category;
	}

	setSelectedApp(app: DiscoveryApp | null): void {
		this.selectedApp = app;
	}

	submitAppForDiscovery(appData: Omit<DiscoveryApp, 'stats' | 'verified' | 'status'>): DiscoveryApp {
		const newApp: DiscoveryApp = {
			...appData,
			verified: false,
			status: 'pending',
			stats: {serverCount: 1},
		};
		runInAction(() => {
			this.customApps = [newApp, ...this.customApps.filter((a) => a.id !== newApp.id)];
			this.saveCustomApps();
		});
		return newApp;
	}

	approveApp(appId: string): void {
		runInAction(() => {
			this.customApps = this.customApps.map((a) =>
				a.id === appId ? {...a, status: 'approved' as const, verified: true} : a,
			);
			this.saveCustomApps();
		});
	}

	rejectApp(appId: string): void {
		runInAction(() => {
			this.customApps = this.customApps.map((a) =>
				a.id === appId ? {...a, status: 'rejected' as const} : a,
			);
			this.saveCustomApps();
		});
	}

	get allApps(): Array<DiscoveryApp> {
		return [...DEFAULT_APPS, ...this.customApps.filter((a) => a.status === 'approved')];
	}

	get pendingApps(): Array<DiscoveryApp> {
		return this.customApps.filter((a) => a.status === 'pending');
	}

	get featuredApps(): Array<DiscoveryApp> {
		return this.allApps.filter((app) => app.featured);
	}

	get filteredApps(): Array<DiscoveryApp> {
		let list = this.allApps;

		if (this.selectedCategory !== 'all') {
			list = list.filter((app) => app.category === this.selectedCategory);
		}

		const query = this.searchQuery.trim().toLowerCase();
		if (query) {
			list = list.filter(
				(app) =>
					app.name.toLowerCase().includes(query) ||
					app.tagline.toLowerCase().includes(query) ||
					app.description.toLowerCase().includes(query) ||
					app.tags.some((tag) => tag.toLowerCase().includes(query)),
			);
		}

		return list;
	}
}

export default new AppDiscoveryState();
